import React, { createContext, useContext } from "react";
import {
  Box,
  Header,
  HelpPanel,
  Link,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";

export type OMSHelpTopic = "rows" | "columns" | "presets" | "copy-presets";

const topics: Record<OMSHelpTopic, { title: string; paragraphs: string[] }> = {
  rows: {
    title: "Rows to export",
    paragraphs: [
      "All rows includes the complete pinned report. Filtered rows includes only rows matching your export filters. Dashboard filters do not apply.",
      "All conditions (AND) requires every filter to match. Any condition (OR) requires at least one.",
    ],
  },
  columns: {
    title: "Columns to export",
    paragraphs: [
      "All columns includes future fields. Selected columns keeps only the fields you choose.",
      "Filters can use fields that you leave out of the CSV. Selected columns appear in your chosen CSV order.",
    ],
  },
  presets: {
    title: "Saved presets",
    paragraphs: [
      "Presets save row filters, column selections, or both for reuse with reports in the same GL and financial account. You can also save a separate preset for another GL or account. Presets are private to you and contain settings, not report data.",
      "Changing export settings does not update a saved preset until you confirm the changes.",
    ],
  },
  "copy-presets": {
    title: "Reuse preset",
    paragraphs: [
      "Reuse a private preset in another GL or financial account by saving a separate version. Changes to either preset do not update the other.",
      "Review the filters and columns against the destination report, then adjust any settings that are unavailable there before saving. The original preset is unchanged.",
    ],
  },
};

export const OMSHelpContext = createContext<
  ((topic: OMSHelpTopic, origin: Element | null) => void) | undefined
>(undefined);

function HelpContents({ topic }: { topic: OMSHelpTopic }) {
  return (
    <SpaceBetween size="m">
      {topics[topic].paragraphs.map((text) => (
        <Box key={text}>{text}</Box>
      ))}
    </SpaceBetween>
  );
}

export function OMSHelpPanel({ topic }: { topic: OMSHelpTopic }) {
  return (
    <HelpPanel
      id="oms-export-help"
      header={<Header variant="h2">{topics[topic].title}</Header>}
    >
      <HelpContents topic={topic} />
    </HelpPanel>
  );
}

export function OMSHelpLink({
  topic,
  label,
}: {
  topic: OMSHelpTopic;
  label: string;
}) {
  const open = useContext(OMSHelpContext);
  const link = (
    <Link
      variant="info"
      href="#oms-export-help"
      ariaLabel={label}
      onFollow={(event) => {
        event.preventDefault();
        open?.(topic, document.activeElement);
      }}
    >
      Info
    </Link>
  );
  // Product-only modal consumers retain their existing self-contained help.
  return open ? (
    link
  ) : (
    <Popover
      header={topics[topic].title}
      dismissAriaLabel={`Close ${topics[topic].title.toLowerCase()} help`}
      triggerType="custom"
      content={<HelpContents topic={topic} />}
    >
      {link}
    </Popover>
  );
}
